import { Directive, EventEmitter, HostBinding, HostListener, Input, Output } from "@angular/core";

/**
 * Shared keyboard-activation wiring for click-only div-based controls (checkbox, date-field,
 * date-picker, dropdown-select, dropdown-search's leading trigger, …): tabindex, aria-disabled,
 * role, and Enter/Space activation. `aria-checked` stays on the host template since it varies per
 * control; `aria-expanded` is handled here (opt-in via appKeyboardActivatableExpanded) and reflects
 * `expanded` as-is regardless of disabled state — a panel can still be open after its trigger
 * becomes disabled (date-picker doesn't close its panel when isDisabled flips), so hiding
 * aria-expanded there would report stale, incorrect state to assistive tech.
 *
 * Set appKeyboardActivatableRole when the host isn't a button — a checkbox-like control needs
 * role="checkbox" plus its own [attr.aria-checked], or assistive tech announces it as a button
 * with no checked state.
 */
@Directive({
  selector: "[appKeyboardActivatable]",
})
export class KeyboardActivatableDirective {
  @Input("appKeyboardActivatableDisabled") disabled = false;
  @Input("appKeyboardActivatableRole") role = "button";
  /** Omit entirely for controls with no expand/collapse panel (e.g. checkbox). */
  @Input("appKeyboardActivatableExpanded") expanded: boolean | null = null;
  @Output("appKeyboardActivatableActivate") activate = new EventEmitter<void>();

  @HostBinding("attr.role")
  get roleAttr(): string {
    return this.role;
  }

  @HostBinding("attr.tabindex")
  get tabindex(): number {
    return this.disabled ? -1 : 0;
  }

  @HostBinding("attr.aria-disabled")
  get ariaDisabled(): boolean | null {
    return this.disabled ? true : null;
  }

  @HostBinding("attr.aria-expanded")
  get ariaExpanded(): boolean | null {
    return this.expanded;
  }

  // preventDefault() must run even on a repeat - otherwise Space's default page-scroll fires on
  // every OS key-repeat tick while the control is held. Only the emit is skipped on repeat, so a
  // held Enter/Space doesn't re-toggle a consumer (like dropdown-select) on every repeat tick.
  @HostListener("keydown.enter", ["$event"])
  @HostListener("keydown.space", ["$event"])
  onActivate(event: KeyboardEvent): void {
    if (this.disabled) {
      return;
    }
    event.preventDefault();
    if (event.repeat) {
      return;
    }
    this.activate.emit();
  }
}
